import { useTranslation } from 'react-i18next';
import type { ISubcontractorProductivityRow } from '@interfaces/IProductivity';
import { ProfessionPill } from '@components/ui/ProfessionPill/ProfessionPill';
import { Building2Icon } from '@utils/icons';
import { formatNumber } from '@utils/formatNumber';
import { CardHeader } from '../../../../shared/CardHeader/CardHeader';
import { ProductivityBar } from '../../Table/ProductivityBar/ProductivityBar';
import { makeSortableTh } from '../../Table/Th/makeSortableTh';
import { NameCell } from '../../Table/NameCell';
import { BreakdownTableFooter } from '../../Table/Footer';
import { useSortableRows } from '../../Table/useSortableRows';
import { usePagination } from '../../Table/usePagination';
import table from '../../Table/breakdownTable.module.css';
import styles from './SubcontractorPanel.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

interface ISubcontractorPanelProps {
  rows: ISubcontractorProductivityRow[];
}

export function SubcontractorPanel({ rows }: ISubcontractorPanelProps) {
  const { t } = useTranslation();
  const sort = useSortableRows(rows, { key: 'name', dir: 'asc' });
  const pager = usePagination(sort.sorted, sort.sortSignature);
  const th = makeSortableTh('subcontractor', sort, t);

  return (
    <>
      <CardHeader
        icon={<Building2Icon size={16} />}
        title={t('productivity.subcontractor.title')}
        meta={t('productivity.subcontractor.meta')}
      />

      <ScrollArea orientation="horizontal">
        <table className={table.table}>
          <colgroup>
            <col className={styles.colName} />
            <col className={styles.colTrade} />
            <col className={styles.colProductivity} />
            <col className={styles.colActual} />
            <col className={styles.colWorkers} />
            <col className={styles.colIssue} />
          </colgroup>
          <thead>
            <tr>
              {th('name', 'productivity.subcontractor.columns.subcontractor')}
              <th className={table.th}>{t('productivity.subcontractor.columns.profession')}</th>
              {th('productivity', 'productivity.subcontractor.columns.productivity')}
              {th('actualWorkDays', 'productivity.subcontractor.columns.actualWorkDays')}
              {th('activeWorkers', 'productivity.subcontractor.columns.activeWorkers')}
              <th className={table.th}>{t('productivity.subcontractor.columns.status')}</th>
            </tr>
          </thead>
          <tbody>
            {pager.visible.map((row) => (
              <tr key={row.id} className={table.tr}>
                <NameCell value={row.name} />
                <td className={table.td}>
                  <span className={table.teamText}>
                    <ProfessionPill profession={row.profession} compact truncate />
                  </span>
                </td>
                <td className={table.td}>
                  <ProductivityBar value={row.productivity} />
                </td>
                <td className={table.td}>{formatNumber(row.actualWorkDays)}</td>
                <td className={table.td}>{formatNumber(row.activeWorkers)}</td>
                <td className={`${table.td} ${table.issue}`}>
                  {row.status ? t(`productivity.status.${row.status}`) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <BreakdownTableFooter pager={pager} />
        </table>
      </ScrollArea>
    </>
  );
}
